import { Divider, Grid, Typography } from "@mui/material";
import React, { useContext, useState } from "react";
import useIsIntegrated from "../../../../../api/hooks/useIsIntegrated";
import ActionStatus from "../../../../../components/ActionStatus";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { IntegrationsMapping } from "../../../../stack/integrations/IntegrationsMapping";
import { PageContext } from "../../../SettingsView";
import { categoryName } from "../../../constants";
import CustomNotificationPage, {
  EnabledMethodsContext,
  SettingsContext,
} from "../../CustomNotificationPage";
import { field_IDs_mapping } from "../../fieldMapping";
import FrequencyNotificationsSection, {
  frequencyType,
} from "./FrequencyNotificationsSection/FrequencyNotificationsSection";
import SlackChannelSelect from "./SlackChannelSelect";

const frequency_fields = field_IDs_mapping.FREQUENCY;

export const SLACK_TOOL = "slack";
export const MICROSOFT_TEAMS_TOOL = "microsoft_teams";

const PageContent = () => {
  const [fetchStatus, setFetchStatus] = useState(null);
  const notificationSettings = useContext(SettingsContext);
  const enabledMethods = useContext(EnabledMethodsContext);
  const [currentCategory] = useContext(PageContext);

  const getSettingsDataByFields = (method, fieldIDs) => {
    const methodSettings = notificationSettings.settings_by_method[method];
    const frequencySettingsData = {};

    for (const field of fieldIDs) {
      if (methodSettings.hasOwnProperty(field)) {
        frequencySettingsData[field] = methodSettings[field];
      }
    }
    return frequencySettingsData;
  };

  // Both isIntegrated queries are already loaded in SettingsView
  const { isIntegrated: isSlackIntegrated } = useIsIntegrated(
    IntegrationsMapping.slack.codeName
  );
  const { isIntegrated: isTeamsIntegrated } = useIsIntegrated(
    IntegrationsMapping.microsoft_teams.codeName
  );

  const anyMethodIsIntegrated =
    (isSlackIntegrated || isTeamsIntegrated) &&
    Object.keys(enabledMethods).length > 0;

  const pageIsEnabled =
    anyMethodIsIntegrated || currentCategory === categoryName.PERSONAL;

  const getMethodsFields = (method) => {
    if (method === SLACK_TOOL) return field_IDs_mapping.SLACK;
    if (method === MICROSOFT_TEAMS_TOOL)
      return field_IDs_mapping.MICROSOFT_TEAMS;
    return {};
  };

  return (
    <>
      {pageIsEnabled ? (
        Object.entries(enabledMethods)
          .filter(([method, isEnabled]) => isEnabled)
          .map(([method, isEnabled], index) => {
            const methods_fields = getMethodsFields(method);
            const allMethodsSettingsExist = Object.values(methods_fields).every(
              (key) => key in notificationSettings.settings_by_method[method]
            );
            return (
              <React.Fragment key={method}>
                {/* Frequency */}
                <Grid item sx={{ marginTop: index > 0 ? 8 : 0 }}>
                  <Grid
                    container
                    direction="row"
                    gap={1}
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <FrequencyNotificationsSection
                        settingsData={getSettingsDataByFields(
                          method,
                          Object.values(frequency_fields)
                        )}
                        setFetchStatus={setFetchStatus}
                        method={method}
                      />
                    </Grid>
                    <Grid item>
                      <ActionStatus
                        status={fetchStatus}
                        setStatus={setFetchStatus}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* Slack channels */}
                {method === SLACK_TOOL && allMethodsSettingsExist && (
                  <>
                    <Grid item>
                      <Divider sx={{ marginTop: 2 }} />
                    </Grid>
                    <Grid item>
                      <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Grid
                          item
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Typography variant="subtitle2">
                            Slack channels
                          </Typography>
                        </Grid>
                        <Grid item>
                          <SlackChannelSelect
                            settingsData={getSettingsDataByFields(
                              method,
                              Object.values(methods_fields)
                            )}
                            setFetchStatus={setFetchStatus}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                {/* Microsoft Teams conversations */}
                {method === MICROSOFT_TEAMS_TOOL && allMethodsSettingsExist && (
                  <>
                    <Grid item>
                      <Divider sx={{ marginTop: 2 }} />
                    </Grid>
                    <Grid item>
                      <Grid container direction="column">
                        <Grid container direction="row" alignItems="center">
                          <SvgToIcon
                            IconLogo={
                              IntegrationsMapping["microsoft_teams"].logo
                            }
                            width="40px"
                          />
                          <Grid
                            item
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Typography variant="subtitle2">
                              Connected Teams
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item sx={{ marginLeft: "40px" }}>
                          {getSettingsDataByFields(method, [
                            field_IDs_mapping.MICROSOFT_TEAMS.CONVERSATIONS,
                          ]).conversations.length > 0 ? (
                            getSettingsDataByFields(method, [
                              field_IDs_mapping.MICROSOFT_TEAMS.CONVERSATIONS,
                            ]).conversations.map((conversation) => (
                              <Typography
                                variant="body2"
                                key={conversation.team_id}
                              >
                                {conversation.team_name}
                              </Typography>
                            ))
                          ) : (
                            <Typography variant="body2">
                              No connected teams
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </React.Fragment>
            );
          })
      ) : (
        <Grid item>
          <Grid
            container
            direction="row"
            gap={1}
            justifyContent="space-between"
          >
            <Grid item>
              <FrequencyNotificationsSection
                settingsData={{
                  frequency_type: frequencyType.DAILY,
                  days: ["Sunday"],
                  time: "10:00:00",
                  timezone: "Asia/Jerusalem",
                }}
                setFetchStatus={() => {}}
                method={SLACK_TOOL}
                disabled={true}
              />
            </Grid>
            <Grid item>
              <ActionStatus status={fetchStatus} setStatus={setFetchStatus} />
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default function GeneralNotificationsPage() {
  return <CustomNotificationPage title="General" PageComponent={PageContent} />;
}
